import { useState, useRef, useCallback, useEffect } from 'react';
import { pcm16Base64ToFloat32, float32ToPcm16Base64 } from '@/lib/audio-utils';

type LiveState = 'idle' | 'connecting' | 'connected' | 'error';

export function useLiveApi({
    onAudioData,
    onTextData,
    onTurnComplete,
    onUserSpeech,
}: {
    onAudioData?: (float32: Float32Array) => void;
    onTextData?: (text: string) => void;
    onTurnComplete?: () => void;
    onUserSpeech?: (text: string) => void;
}) {
    const [state, setState] = useState<LiveState>('idle');
    const wsRef = useRef<WebSocket | null>(null);
    const mediaRecorderRef = useRef<MediaRecorder | null>(null);
    const audioContextRef = useRef<AudioContext | null>(null);
    const workletNodeRef = useRef<AudioWorkletNode | null>(null);
    const streamRef = useRef<MediaStream | null>(null);

    const connect = useCallback(async (systemInstruction: string) => {
        setState('connecting');

        try {
            const apiKey = process.env.NEXT_PUBLIC_GEMINI_API_KEY;
            if (!apiKey) throw new Error("Missing NEXT_PUBLIC_GEMINI_API_KEY");

            const wsUrl = `wss://generativelanguage.googleapis.com/ws/google.ai.generativelanguage.v1alpha.GenerativeService.BidiGenerateContent?key=${apiKey}`;
            const ws = new WebSocket(wsUrl);
            wsRef.current = ws;

            ws.onopen = () => {
                console.log("✅ [LiveAPI] Connected to Gemini Live API");
                setState('connected');

                // Initial setup message
                const setupMsg = {
                    setup: {
                        model: "models/gemini-2.5-flash-native-audio-preview-12-2025",
                        generationConfig: {
                            responseModalities: ["AUDIO"],
                        },
                        systemInstruction: {
                            parts: [{ text: systemInstruction }]
                        },
                        inputAudioTranscription: {},
                        outputAudioTranscription: {},
                    }
                };
                console.log("📤 [LiveAPI] Sending setup:", setupMsg);
                ws.send(JSON.stringify(setupMsg));
            };

            ws.onmessage = async (event) => {
                let data;
                if (event.data instanceof Blob) {
                    data = JSON.parse(await event.data.text());
                } else {
                    data = JSON.parse(event.data);
                }

                // Handle server audio
                if (data.serverContent?.modelTurn?.parts) {
                    for (const part of data.serverContent.modelTurn.parts) {
                        if (part.inlineData && part.inlineData.mimeType.startsWith('audio/pcm')) {
                            const pcmData = part.inlineData.data;
                            const float32 = pcm16Base64ToFloat32(pcmData);
                            onAudioData?.(float32);
                        }
                        // Don't use part.text here — it contains chain-of-thought thinking
                    }
                }

                // Capture input transcription (what user said)
                if (data.serverContent?.inputTranscription?.text) {
                    onUserSpeech?.(data.serverContent.inputTranscription.text);
                }

                // Capture output transcription (what model said)
                if (data.serverContent?.outputTranscription?.text) {
                    onTextData?.(data.serverContent.outputTranscription.text);
                }

                // Detect turn completion (model finished speaking)
                if (data.serverContent?.turnComplete) {
                    onTurnComplete?.();
                }
            };

            ws.onerror = (e) => {
                console.error("❌ [LiveAPI] WebSocket error:", e);
                setState('error');
            };

            ws.onclose = (e) => {
                console.log(`🔌 [LiveAPI] WebSocket closed. Code: ${e.code}, Reason: ${e.reason}`);
                if (state !== 'idle') setState('idle');
            };

            // Start Microphone
            await startMicrophone(ws);

        } catch (e) {
            console.error("❌ [LiveAPI] Connection failed:", e);
            setState('error');
        }
    }, [onAudioData, onTextData, onTurnComplete, onUserSpeech, state]);

    const startMicrophone = async (ws: WebSocket) => {
        try {
            const stream = await navigator.mediaDevices.getUserMedia({
                audio: {
                    sampleRate: 16000,
                    channelCount: 1,
                    echoCancellation: true,
                    autoGainControl: true,
                    noiseSuppression: true
                }
            });
            streamRef.current = stream;

            const audioContext = new AudioContext({ sampleRate: 16000 });
            if (audioContext.state === 'suspended') {
                console.log("🔊 [LiveAPI] Resuming AudioContext...");
                await audioContext.resume();
            }
            audioContextRef.current = audioContext;

            const source = audioContext.createMediaStreamSource(stream);

            // We need raw PCM data. ScriptProcessor is deprecated but easiest for vanilla JS.
            // Better to use AudioWorklet if possible, but let's stick to simple implementation first.
            const processor = audioContext.createScriptProcessor(512, 1, 1);

            processor.onaudioprocess = (e) => {
                if (ws.readyState !== WebSocket.OPEN) return;

                const inputData = e.inputBuffer.getChannelData(0);
                const base64 = float32ToPcm16Base64(inputData);

                const msg = {
                    realtimeInput: {
                        mediaChunks: [{
                            mimeType: "audio/pcm;rate=16000",
                            data: base64
                        }]
                    }
                };
                ws.send(JSON.stringify(msg));
            };

            source.connect(processor);
            processor.connect(audioContext.destination); // destination is needed for script processor to run
        } catch (err) {
            console.error("Mic error:", err);
        }
    };

    const disconnect = useCallback(() => {
        if (wsRef.current) {
            wsRef.current.close();
            wsRef.current = null;
        }
        if (streamRef.current) {
            streamRef.current.getTracks().forEach(track => track.stop());
            streamRef.current = null;
        }
        if (audioContextRef.current) {
            audioContextRef.current.close();
            audioContextRef.current = null;
        }
        setState('idle');
    }, []);

    useEffect(() => {
        return () => disconnect();
    }, [disconnect]);

    return { connect, disconnect, state };
}
